import { NgClass, NgIf, NgForOf } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, inject, isDevMode, InjectionToken, signal, computed, Component } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import * as i1 from '@angular/material/tooltip';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as i1$1 from '@angular/router';
import { Router, NavigationEnd, RouterLink } from '@angular/router';
import { ConfigService } from '@rxap/config';
import { from, startWith, Subject, switchMap, debounceTime, merge, interval, take, takeUntil, firstValueFrom, skip } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { OpenApiHttpResponseError } from '@rxap/open-api';
import * as Sentry from '@sentry/angular';
import { __decorate } from 'tslib';
import { OpenApiRemoteMethod, RxapOpenApiRemoteMethod } from '@rxap/open-api/remote-method';

let StatusControllerHealthCheckRemoteMethod = class StatusControllerHealthCheckRemoteMethod extends OpenApiRemoteMethod {
    call(parameters) {
        return super.call(parameters);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: StatusControllerHealthCheckRemoteMethod, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: StatusControllerHealthCheckRemoteMethod, providedIn: 'root' }); }
};
StatusControllerHealthCheckRemoteMethod = __decorate([
    RxapOpenApiRemoteMethod({
        serverId: 'service-status',
        operationId: 'StatusController_healthCheck',
        operation: `{
  "operationId": "StatusController_healthCheck",
  "parameters": [
    {
      "name": "service",
      "required": true,
      "in": "query",
      "schema": {
        "type": "array",
        "items": {
          "type": "string"
        }
      }
    }
  ],
  "responses": {
    "200": {
      "content": {
        "application/json": {
          "schema": {
            "type": "object",
            "properties": {
              "status": {
                "type": "string"
              },
              "info": {
                "type": "object",
                "additionalProperties": {
                  "type": "object",
                  "properties": {
                    "status": {
                      "type": "string"
                    }
                  },
                  "additionalProperties": {
                    "type": "string"
                  }
                },
                "nullable": true
              },
              "error": {
                "type": "object",
                "additionalProperties": {
                  "type": "object",
                  "properties": {
                    "status": {
                      "type": "string"
                    }
                  },
                  "additionalProperties": {
                    "type": "string"
                  }
                },
                "nullable": true
              },
              "details": {
                "type": "object",
                "additionalProperties": {
                  "type": "object",
                  "properties": {
                    "status": {
                      "type": "string"
                    }
                  },
                  "additionalProperties": {
                    "type": "string"
                  }
                }
              }
            }
          }
        }
      }
    }
  },
  "method": "get",
  "path": "/many"
}`
    })
], StatusControllerHealthCheckRemoteMethod);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: StatusControllerHealthCheckRemoteMethod, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class StatusCheckService {
    constructor() {
        this.getServiceStatusMethod = inject(StatusControllerHealthCheckRemoteMethod);
    }
    getStatus(serviceNames) {
        if (serviceNames.length === 0) {
            return from(Promise.resolve({ status: 'empty' }));
        }
        const loading = {
            status: 'loading',
            info: serviceNames.reduce((acc, serviceName) => ({
                ...acc,
                [serviceName]: { status: 'loading' },
            }), {}),
        };
        return from(this.requestStatus(serviceNames)).pipe(startWith(loading));
    }
    async requestStatus(serviceNames) {
        if (serviceNames.length === 0) {
            return { status: 'empty' };
        }
        let status = { status: 'fatal' };
        try {
            status = await this.getServiceStatusMethod.call({ parameters: { service: serviceNames.slice() } });
        }
        catch (error) {
            status = this.handleStatusCheckError(error, status, serviceNames);
        }
        status.status ??= 'unknown';
        return status;
    }
    handleStatusCheckError(error, status, serviceNames) {
        if (error instanceof OpenApiHttpResponseError) {
            switch (error.status) {
                case 503:
                    Sentry.captureMessage(`API is unhealthy`, {
                        level: 'error',
                        contexts: {
                            response: {
                                status: error.status,
                                message: error.message,
                                operationId: error.operationId,
                            },
                            status: error.httpErrorResponse.error,
                            services: {
                                text: serviceNames.join(', '),
                                serviceNames,
                            },
                        },
                    });
                    status = error.httpErrorResponse.error;
                    break;
                case 500:
                    Sentry.captureMessage(`Internal Server Error while requesting api status`, {
                        level: 'fatal',
                        contexts: {
                            response: {
                                status: error.status,
                                message: error.message,
                                operationId: error.operationId,
                            },
                            services: {
                                text: serviceNames.join(', '),
                                serviceNames,
                            },
                        },
                    });
                    if (isDevMode()) {
                        console.error(`Internal Server Error while requesting api status`, error);
                    }
                    status = { status: 'internal-server-error' };
                    break;
                case 502:
                    Sentry.captureMessage(`Bad gateway while requesting api status`, {
                        level: 'fatal',
                        contexts: {
                            response: {
                                status: error.status,
                                message: error.message,
                                operationId: error.operationId,
                            },
                            services: {
                                text: serviceNames.join(', '),
                                serviceNames,
                            },
                        },
                    });
                    if (isDevMode()) {
                        console.error(`Bad gateway while requesting api status`, error);
                    }
                    status = { status: 'bad-gateway' };
                    break;
                case 504:
                    Sentry.captureMessage(`Gateway timeout while requesting api status`, {
                        level: 'fatal',
                        contexts: {
                            response: {
                                status: error.status,
                                message: error.message,
                                operationId: error.operationId,
                            },
                            services: {
                                text: serviceNames.join(', '),
                                serviceNames,
                            },
                        },
                    });
                    if (isDevMode()) {
                        console.error(`Gateway timeout while requesting api status`, error);
                    }
                    status = { status: 'gateway-timeout' };
                    break;
                case 405:
                    Sentry.captureMessage(`Method not found while requesting api status`, {
                        level: 'fatal',
                        contexts: {
                            response: {
                                status: error.status,
                                message: error.message,
                                operationId: error.operationId,
                            },
                            services: {
                                text: serviceNames.join(', '),
                                serviceNames,
                            },
                        },
                    });
                    if (isDevMode()) {
                        console.error(`Method not found while requesting api status`, error);
                    }
                    status = { status: 'method-not-found' };
                    break;
                case 401:
                    Sentry.captureMessage(`Unauthorized while requesting api status`, {
                        level: 'fatal',
                        contexts: {
                            response: {
                                status: error.status,
                                message: error.message,
                                operationId: error.operationId,
                            },
                            services: {
                                text: serviceNames.join(', '),
                                serviceNames,
                            },
                        },
                    });
                    if (isDevMode()) {
                        console.error(`Unauthorized while requesting api status`, error);
                    }
                    status = { status: 'unauthorized' };
                    break;
                case 429:
                    Sentry.captureMessage(`Rate limiting while requesting api status`, {
                        level: 'fatal',
                        contexts: {
                            response: {
                                status: error.status,
                                message: error.message,
                                operationId: error.operationId,
                            },
                            services: {
                                text: serviceNames.join(', '),
                                serviceNames,
                            },
                        },
                    });
                    if (isDevMode()) {
                        console.error(`Rate limiting while requesting api status`, error);
                    }
                    status = { status: 'rate-limiting' };
                    break;
                default:
                    Sentry.captureMessage(`An unexpected HTTP Status code while requesting api status`, {
                        level: 'fatal',
                        contexts: {
                            response: {
                                status: error.status,
                                message: error.message,
                                operationId: error.operationId,
                            },
                            services: {
                                text: serviceNames.join(', '),
                                serviceNames,
                            },
                        },
                    });
                    if (isDevMode()) {
                        console.error(`An unexpected HTTP Status code while requesting api status`, error);
                    }
                    status = { status: 'unknown' };
                    break;
            }
        }
        else {
            Sentry.captureMessage(`API status request failed: ${error.message}`, {
                level: 'fatal',
                contexts: {
                    error: {
                        message: error.message,
                        stack: error.stack,
                    },
                    services: {
                        text: serviceNames.join(', '),
                        serviceNames,
                    },
                },
            });
            if (isDevMode()) {
                console.error(`API status request failed`, error);
            }
        }
        return status;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: StatusCheckService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: StatusCheckService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: StatusCheckService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const STATUS_INDICATOR_INTERVAL = new InjectionToken('status-indicator-interval', {
    providedIn: 'root',
    factory: () => isDevMode() ? 60 : 60 * 5,
});
const STATUS_CHECK_INTERVAL = new InjectionToken('status-check-interval', {
    providedIn: 'root',
    factory: () => isDevMode() ? 5 : 60,
});

class StatusIndicatorComponent {
    constructor() {
        this.services = [];
        this.countdown = signal(inject(STATUS_INDICATOR_INTERVAL));
        this.statusIndicatorInterval = inject(STATUS_INDICATOR_INTERVAL);
        this.config = inject(ConfigService);
        this.statusCheckService = inject(StatusCheckService);
        this.router = inject(Router);
        /**
         * used to interrupt the countdown on a component destruction
         */
        this.destroy$ = new Subject();
        this.retry$ = new Subject();
        this.services = Object
            .entries(this.config.get('api', {}))
            .filter(([_, value]) => typeof value === 'object' && value && !!value['statusCheck'])
            .map(([key]) => key);
        this.queryParams = toSignal(this.router.events.pipe(filter((event) => event instanceof NavigationEnd), map(event => ({
            url: event.urlAfterRedirects,
            service: this.services,
        }))));
        const status$ = this.statusCheckService.getStatus(this.services).pipe(map(status => status.status));
        const manualRetry$ = this.retry$.pipe(switchMap(() => this.statusCheckService
            .getStatus(this.services)
            .pipe(map(status => status.status), debounceTime(500))));
        this.status = toSignal(merge(status$, manualRetry$), { initialValue: 'loading' });
        this.tooltip = computed(() => {
            const status = this.status();
            if ($localize && typeof $localize === 'function') {
                return $localize `:@@status-check-tooltip:app status` + `: ${status}`;
            }
            else {
                return `app status: ${status}`;
            }
        });
    }
    initiateCountdown() {
        this.subscription = interval(1000)
            .pipe(take(this.statusIndicatorInterval), takeUntil(this.destroy$))
            .subscribe({
            next: val => this.countdown.set(this.statusIndicatorInterval - val),
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            error: () => { },
            complete: () => {
                this.retry$.next();
                this.countdown.set(this.statusIndicatorInterval);
                // Your API check logic here
                this.initiateCountdown();
            },
        });
    }
    ngOnInit() {
        this.initiateCountdown();
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.subscription?.unsubscribe();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: StatusIndicatorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.0.1", type: StatusIndicatorComponent, isStandalone: true, selector: "rxap-status-indicator", ngImport: i0, template: "<button [matTooltip]=\"tooltip()\" [queryParams]=\"queryParams()\" [routerLink]=\"['/', 'status-check']\" class=\"px-3 py-3\">\n  <span class=\"flex flex-row gap-4 items-center\">\n    <span [ngClass]=\"{\n    'bg-red-500': status() === 'error',\n    'bg-green-500': status() === 'ok',\n    'bg-yellow-500': status() === 'loading',\n    'bg-black': !['error', 'ok', 'loading'].includes(status()),\n    }\" class=\"indicator w-3 h-3 rounded-full\"></span>\n  </span>\n</button>\n", styles: [".indicator{border-radius:100%}\n"], dependencies: [{ kind: "directive", type: RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "ngmodule", type: MatTooltipModule }, { kind: "directive", type: i1.MatTooltip, selector: "[matTooltip]", inputs: ["matTooltipPosition", "matTooltipPositionAtOrigin", "matTooltipDisabled", "matTooltipShowDelay", "matTooltipHideDelay", "matTooltipTouchGestures", "matTooltip", "matTooltipClass"], exportAs: ["matTooltip"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: StatusIndicatorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rxap-status-indicator', standalone: true, imports: [RouterLink, NgClass, MatTooltipModule], template: "<button [matTooltip]=\"tooltip()\" [queryParams]=\"queryParams()\" [routerLink]=\"['/', 'status-check']\" class=\"px-3 py-3\">\n  <span class=\"flex flex-row gap-4 items-center\">\n    <span [ngClass]=\"{\n    'bg-red-500': status() === 'error',\n    'bg-green-500': status() === 'ok',\n    'bg-yellow-500': status() === 'loading',\n    'bg-black': !['error', 'ok', 'loading'].includes(status()),\n    }\" class=\"indicator w-3 h-3 rounded-full\"></span>\n  </span>\n</button>\n", styles: [".indicator{border-radius:100%}\n"] }]
        }], ctorParameters: () => [] });

class StatusCheckComponent {
    constructor(route) {
        this.route = route;
        this.showRedirectWarning = signal(false);
        this.countdown = signal(inject(STATUS_CHECK_INTERVAL));
        this.statusCheckInterval = inject(STATUS_CHECK_INTERVAL);
        /**
         * used to interrupt the countdown on a component destruction
         */
        this.destroy$ = new Subject();
        this.retry$ = new Subject();
        this.statusCheckService = inject(StatusCheckService);
        this.url = toSignal(this.route.queryParamMap.pipe(map(map => map.get('url'))), { initialValue: null });
        const status$ = this.route.queryParamMap.pipe(map(map => map.getAll('service')), switchMap(services => this.statusCheckService.getStatus(services)), debounceTime(100));
        const manualRetry$ = this.retry$.pipe(switchMap(() => status$));
        this.status = toSignal(merge(status$, manualRetry$), { initialValue: { status: 'initial' } });
        this.services = computed(() => {
            const status = this.status();
            return [
                ...Object.entries(status.info ?? {})
                    .map(([name, status]) => ({
                    name,
                    status: status.status,
                    message: status['message'],
                })),
                ...Object.entries(status.error ?? {})
                    .map(([name, status]) => ({
                    name,
                    status: status.status,
                    message: status['message'],
                })),
            ].sort((a, b) => a.name.localeCompare(b.name));
        });
        this.statusIsOk = computed(() => this.status().status === 'ok');
        this.statusIsError = computed(() => this.status().status === 'error');
        this.statusIsEmpty = computed(() => this.status().status === 'empty');
        this.statusIsFatal = computed(() => this.status().status === 'fatal');
        this.statusIsAvailable =
            computed(() => ['ok', 'error', 'loading'].includes(this.status().status ?? 'unavailable'));
        this.statusIsUnavailable = computed(() => !this.statusIsAvailable() &&
            !['empty', 'fatal'].includes(this.status().status ?? 'unavailable'));
    }
    ngOnInit() {
        this.initiateCountdown();
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.subscription?.unsubscribe();
    }
    initiateCountdown() {
        this.subscription = interval(1000)
            .pipe(take(this.statusCheckInterval), takeUntil(this.destroy$))
            .subscribe({
            next: val => this.countdown.set(this.statusCheckInterval - val),
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            error: () => { },
            complete: () => {
                this.retry$.next();
                this.countdown.set(this.statusCheckInterval);
                // Your API check logic here
                this.initiateCountdown();
            },
        });
    }
    onCancel() {
        this.showRedirectWarning.set(false);
    }
    openWarningDialog() {
        this.showRedirectWarning.set(true);
    }
    trackBy(index, item) {
        return item.name;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: StatusCheckComponent, deps: [{ token: i1$1.ActivatedRoute }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.0.1", type: StatusCheckComponent, isStandalone: true, selector: "rxap-status-check", ngImport: i0, template: "<div class=\"flex flex-col items-center justify-center min-h-screen mx-auto\">\n\n  <div class=\"bg-white dark:bg-black rounded-lg shadow-lg p-8 w-full md:w-1/2 m-8\">\n    <h2 class=\"text-2xl font-semibold mb-4\" i18n>API Status Check</h2>\n\n    <hr class=\"mb-4\">\n\n\n    <div *ngIf=\"statusIsAvailable()\">\n      <h3 class=\"text-xl font-semibold mb-2\" i18n>Services</h3>\n      <div>\n        <table class=\"min-w-full border border-gray-300\">\n          <tbody>\n          <tr *ngFor=\"let service of services(); trackBy: trackBy\" [ngClass]=\"{\n              'bg-green-100 dark:bg-green-900': service.status === 'up',\n              'bg-red-100 dark:bg-red-900': service.status === 'down',\n              'bg-yellow-100 dark:bg-yellow-900': service.status === 'loading',\n            }\" class=\"border-b border-gray-300\">\n            <th class=\"p-2 text-left\">{{ service.name }}</th>\n            <td class=\"p-2 text-center\">{{ service.message }}</td>\n            <td [ngClass]=\"{\n              'text-green-700 dark:text-green-300': service.status === 'up',\n              'text-red-700 dark:text-red-300': service.status === 'down',\n              'text-yellow-700 dark:text-yellow-300': service.status === 'loading',\n            }\" class=\"p-2 text-right font-bold\">{{ service.status }}</td>\n          </tr>\n          </tbody>\n        </table>\n      </div>\n    </div>\n\n    <div *ngIf=\"statusIsEmpty()\">\n      <p class=\"text-center\" i18n>No services are currently selected.</p>\n    </div>\n\n    <div *ngIf=\"statusIsUnavailable()\">\n      <p class=\"text-center\" i18n>Failed to retrieve status for selected services: <b>{{ status().status }}</b></p>\n    </div>\n\n    <div *ngIf=\"statusIsFatal()\">\n      <p class=\"text-center\"\n         i18n>Something went unexpectedly wrong. Try reloading the page or contact an administrator.</p>\n    </div>\n\n    <hr class=\"my-4\">\n\n    <ng-container *ngIf=\"services().length\">\n    <div class=\"flex justify-center items-center\">\n      <p class=\"text-lg font-medium\">\n        <ng-container i18n>Next try in</ng-container>&nbsp;\n        <span class=\"font-bold text-blue-500\">{{ countdown() }}</span>s\n      </p>\n    </div>\n\n    <hr class=\"my-4\">\n    </ng-container>\n\n    <div *ngIf=\"url() as url\">\n      <h4 class=\"text-lg font-semibold mb-2\" i18n>Previous Route</h4>\n      <p class=\"mb-2\" i18n>To return to the previous page, click the link below:</p>\n      <a *ngIf=\"!statusIsError(); else statusNoOk\" [routerLink]=\"url\"\n         class=\"cursor-pointer text-blue-500 hover:underline\">\n        <ng-container i18n>Go Back to</ng-container>&nbsp;<i>{{ url }}</i></a>\n      <ng-template #statusNoOk>\n        <a (click)=\"openWarningDialog()\" class=\"cursor-pointer text-blue-500 hover:underline\">\n          <ng-container i18n>Go Back to</ng-container>&nbsp;<i>{{ url }}</i></a>\n      </ng-template>\n    </div>\n\n  </div>\n\n</div>\n\n<div *ngIf=\"showRedirectWarning()\" class=\"fixed inset-0 flex items-center justify-center z-50 shadow-lg\">\n  <div class=\"bg-white dark:bg-black p-6 rounded shadow-lg\">\n    <h1 class=\"text-xl font-semibold mb-4\" i18n>Warning</h1>\n    <p class=\"w-96\" i18n><b>Caution</b>: Some services are currently <b\n      class=\"text-red-500\">down</b>. If you proceed, you will be redirected to the application's home page.\n      <b>Are you sure you wish to continue?</b></p>\n    <div class=\"flex justify-end mt-4\">\n      <button [routerLink]=\"['/']\" class=\"text-white bg-red-500 px-4 py-2 rounded\" i18n>Confirm</button>\n      <button (click)=\"onCancel()\" class=\"text-gray-600 ml-2 px-4 py-2 rounded\" i18n>Cancel</button>\n    </div>\n  </div>\n</div>\n", styles: [""], dependencies: [{ kind: "directive", type: RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: StatusCheckComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rxap-status-check', standalone: true, imports: [RouterLink, NgIf, NgClass, NgForOf], template: "<div class=\"flex flex-col items-center justify-center min-h-screen mx-auto\">\n\n  <div class=\"bg-white dark:bg-black rounded-lg shadow-lg p-8 w-full md:w-1/2 m-8\">\n    <h2 class=\"text-2xl font-semibold mb-4\" i18n>API Status Check</h2>\n\n    <hr class=\"mb-4\">\n\n\n    <div *ngIf=\"statusIsAvailable()\">\n      <h3 class=\"text-xl font-semibold mb-2\" i18n>Services</h3>\n      <div>\n        <table class=\"min-w-full border border-gray-300\">\n          <tbody>\n          <tr *ngFor=\"let service of services(); trackBy: trackBy\" [ngClass]=\"{\n              'bg-green-100 dark:bg-green-900': service.status === 'up',\n              'bg-red-100 dark:bg-red-900': service.status === 'down',\n              'bg-yellow-100 dark:bg-yellow-900': service.status === 'loading',\n            }\" class=\"border-b border-gray-300\">\n            <th class=\"p-2 text-left\">{{ service.name }}</th>\n            <td class=\"p-2 text-center\">{{ service.message }}</td>\n            <td [ngClass]=\"{\n              'text-green-700 dark:text-green-300': service.status === 'up',\n              'text-red-700 dark:text-red-300': service.status === 'down',\n              'text-yellow-700 dark:text-yellow-300': service.status === 'loading',\n            }\" class=\"p-2 text-right font-bold\">{{ service.status }}</td>\n          </tr>\n          </tbody>\n        </table>\n      </div>\n    </div>\n\n    <div *ngIf=\"statusIsEmpty()\">\n      <p class=\"text-center\" i18n>No services are currently selected.</p>\n    </div>\n\n    <div *ngIf=\"statusIsUnavailable()\">\n      <p class=\"text-center\" i18n>Failed to retrieve status for selected services: <b>{{ status().status }}</b></p>\n    </div>\n\n    <div *ngIf=\"statusIsFatal()\">\n      <p class=\"text-center\"\n         i18n>Something went unexpectedly wrong. Try reloading the page or contact an administrator.</p>\n    </div>\n\n    <hr class=\"my-4\">\n\n    <ng-container *ngIf=\"services().length\">\n    <div class=\"flex justify-center items-center\">\n      <p class=\"text-lg font-medium\">\n        <ng-container i18n>Next try in</ng-container>&nbsp;\n        <span class=\"font-bold text-blue-500\">{{ countdown() }}</span>s\n      </p>\n    </div>\n\n    <hr class=\"my-4\">\n    </ng-container>\n\n    <div *ngIf=\"url() as url\">\n      <h4 class=\"text-lg font-semibold mb-2\" i18n>Previous Route</h4>\n      <p class=\"mb-2\" i18n>To return to the previous page, click the link below:</p>\n      <a *ngIf=\"!statusIsError(); else statusNoOk\" [routerLink]=\"url\"\n         class=\"cursor-pointer text-blue-500 hover:underline\">\n        <ng-container i18n>Go Back to</ng-container>&nbsp;<i>{{ url }}</i></a>\n      <ng-template #statusNoOk>\n        <a (click)=\"openWarningDialog()\" class=\"cursor-pointer text-blue-500 hover:underline\">\n          <ng-container i18n>Go Back to</ng-container>&nbsp;<i>{{ url }}</i></a>\n      </ng-template>\n    </div>\n\n  </div>\n\n</div>\n\n<div *ngIf=\"showRedirectWarning()\" class=\"fixed inset-0 flex items-center justify-center z-50 shadow-lg\">\n  <div class=\"bg-white dark:bg-black p-6 rounded shadow-lg\">\n    <h1 class=\"text-xl font-semibold mb-4\" i18n>Warning</h1>\n    <p class=\"w-96\" i18n><b>Caution</b>: Some services are currently <b\n      class=\"text-red-500\">down</b>. If you proceed, you will be redirected to the application's home page.\n      <b>Are you sure you wish to continue?</b></p>\n    <div class=\"flex justify-end mt-4\">\n      <button [routerLink]=\"['/']\" class=\"text-white bg-red-500 px-4 py-2 rounded\" i18n>Confirm</button>\n      <button (click)=\"onCancel()\" class=\"text-gray-600 ml-2 px-4 py-2 rounded\" i18n>Cancel</button>\n    </div>\n  </div>\n</div>\n" }]
        }], ctorParameters: () => [{ type: i1$1.ActivatedRoute }] });

let StatusControllerHealthCheckServiceRemoteMethod = class StatusControllerHealthCheckServiceRemoteMethod extends OpenApiRemoteMethod {
    call(parameters) {
        return super.call(parameters);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: StatusControllerHealthCheckServiceRemoteMethod, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: StatusControllerHealthCheckServiceRemoteMethod, providedIn: 'root' }); }
};
StatusControllerHealthCheckServiceRemoteMethod = __decorate([
    RxapOpenApiRemoteMethod({
        serverId: 'service-status',
        operationId: 'StatusController_healthCheckService',
        operation: `{
  "operationId": "StatusController_healthCheckService",
  "parameters": [
    {
      "name": "name",
      "required": true,
      "in": "path",
      "schema": {
        "type": "string"
      }
    }
  ],
  "responses": {
    "200": {
      "content": {
        "application/json": {
          "schema": {
            "type": "object",
            "properties": {
              "status": {
                "type": "string"
              },
              "info": {
                "type": "object",
                "additionalProperties": {
                  "type": "object",
                  "properties": {
                    "status": {
                      "type": "string"
                    }
                  },
                  "additionalProperties": {
                    "type": "string"
                  }
                },
                "nullable": true
              },
              "error": {
                "type": "object",
                "additionalProperties": {
                  "type": "object",
                  "properties": {
                    "status": {
                      "type": "string"
                    }
                  },
                  "additionalProperties": {
                    "type": "string"
                  }
                },
                "nullable": true
              },
              "details": {
                "type": "object",
                "additionalProperties": {
                  "type": "object",
                  "properties": {
                    "status": {
                      "type": "string"
                    }
                  },
                  "additionalProperties": {
                    "type": "string"
                  }
                }
              }
            }
          }
        }
      }
    }
  },
  "method": "get",
  "path": "/{name}"
}`,
    })
], StatusControllerHealthCheckServiceRemoteMethod);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.1", ngImport: i0, type: StatusControllerHealthCheckServiceRemoteMethod, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

function ProvideStatusCheck(...withProvider) {
    return withProvider.flat();
}
function withStatusCheckInterval(interval) {
    return {
        provide: STATUS_CHECK_INTERVAL,
        useValue: interval,
    };
}
function withStatusIndicatorInterval(interval) {
    return {
        provide: STATUS_INDICATOR_INTERVAL,
        useValue: interval,
    };
}

const StatusCheckGuard = async (route) => {
    const statusCheckService = inject(StatusCheckService);
    const router = inject(Router);
    const statusCheck = route.data['statusCheck'];
    const url = '/' + route.url.join('/');
    if (!statusCheck || !statusCheck.services?.length) {
        if (isDevMode()) {
            console.log(`No status check services defined in route data: '${url}'`);
        }
        return true;
    }
    if (isDevMode()) {
        console.log(`Status check: ${url} for services: [ ${statusCheck.services.join(', ')} ]`);
    }
    const status = await firstValueFrom(statusCheckService.getStatus(statusCheck.services).pipe(skip(1)));
    if (status.status === 'ok') {
        if (isDevMode()) {
            console.log(`Status check OK: ${url} for services: [ ${statusCheck.services.join(', ')} ]`);
        }
        return true;
    }
    console.error(`Status check failed: ${url} for services: [ ${statusCheck.services.join(', ')} ]`);
    return router.createUrlTree(['/', 'status-check'], {
        queryParams: {
            url,
            service: statusCheck.services,
        },
    });
};

const STATUS_CHECK_ROUTE = {
    component: StatusCheckComponent,
    path: 'status-check',
};

// region status-indicator
// endregion

/**
 * Generated bundle index. Do not edit.
 */

export { ProvideStatusCheck, STATUS_CHECK_INTERVAL, STATUS_CHECK_ROUTE, STATUS_INDICATOR_INTERVAL, StatusCheckComponent, StatusCheckGuard, StatusCheckService, StatusControllerHealthCheckRemoteMethod, StatusControllerHealthCheckServiceRemoteMethod, StatusIndicatorComponent, withStatusCheckInterval, withStatusIndicatorInterval };
